/* **The ember primary — one treatment, every surface that draws one.**
 *
 * The library header's `+ Nowy program`, the empty state's and the no-results state's, the create
 * modal's `Dalej` and `Utwórz i otwórz kreator`, and the builder bar's `Zapisz` were five copies of
 * the same declaration block differing by a few pixels — which is precisely the failure the house
 * rule names ("the same declaration block in two features moves to `shared/ui`"), and it had already
 * cost real divergence: each copy carried its own hover, its own ink and its own justification, and
 * when the owner reversed the ink on 5 August 2026 there were five places to reverse it in.
 *
 * A stylesheet with no component, like `MenuSurface.module.css` beside it. A component would have to
 * take the geometry as props — four heights, four radii, four type steps — which is a prop bag, and
 * it would put a React boundary around a thing that is one `<button>` on every surface.
 *
 * ── How a surface uses it ────────────────────────────────────────────────────────────────────
 * Two classes on the element: this one for the TREATMENT — fill, ink, border, transition and the
 * three states — and the surface's own for the GEOMETRY, declared there as ORDINARY PROPERTIES:
 *
 *   height          padding         border-radius
 *   font-size       font-weight     letter-spacing
 *
 * **Nothing may be declared on both sides, and that is a rule rather than an observation.** The
 * earlier wording here claimed there "is no specificity race" — a statement about the arrangement
 * that was simply false of the code: `ProgressionDrawer.module.css` listed `.apply:focus-visible` in
 * a group setting `outline: … var(--accent)` while `.apply` also wore `.ember`, whose
 * `:focus-visible` sets `var(--text)`. Two single-class selectors, equal specificity, in two files.
 *
 * The tie is then broken by the order the STYLESHEETS land in the document, not by the order of the
 * imports in the component — this file is a shared module and gets a CSS chunk of its own. Measured
 * by walking `document.styleSheets` on 5 August 2026, and the two environments disagreed:
 *
 * | | index of `emberButton` | index of the surface's chunk | ring that won |
 * |---|---|---|---|
 * | `vite build` + preview | 0, `<link>`ed from `index.html` | 1, the route chunk | the SURFACE's, accent |
 * | `npm run dev` | injected after the component's | before it | this file's, neutral |
 *
 * So the drawer's primary drew a different focus ring in development and in the shipped bundle, and
 * neither was reviewable from the diff. That is the whole argument for the rule.
 *
 * So: a surface declares the six geometry properties and NOTHING this file declares — not
 * `background`, not `color`, not `border-*`, and not a `:focus-visible` outline.
 *
 * ── They are properties rather than `--cta-*` knobs, and the difference is enforcement ───────
 * An earlier revision took all six as custom properties and this comment claimed the arrangement
 * was safe because "the Stylelint value allowlist accepts a bare token reference and nothing else".
 * That is true of the allowlist and false of the arrangement: `declaration-property-value-allowed-
 * list` is keyed on REAL property names, so `padding: 17px` fails the build and `--cta-inline: 17px`
 * passes it untouched. Six of the gated properties in this app had quietly stepped outside the gate
 * — proved with a probe on 5 August 2026, not argued. Declared as `padding` and `font-size` on the
 * surface, every one of them is checked again.
 *
 * All six are still REQUIRED of a surface. A surface that forgets one now inherits rather than
 * leaving the property unset, which is the one thing this shape is slightly worse at — and it is
 * visible immediately either way.
 *
 * ── Three rules that do not bend ─────────────────────────────────────────────────────────────
 * · **The ink is WHITE** (`--on-accent-grad`, reversed to #ffffff by owner directive). It does not
 *   meet AA on this gradient; the token's own comment records the measurement and the approval, and
 *   this is not a thing to quietly change back.
 * · **The gradient is never replaced.** The pressed state is `--cta-press`, a neutral
 *   `brightness()`. Swapping `background` to a flat colour was the previous spelling and it FLICKERS
 *   — a background-image and a background-color cannot interpolate, so the browser snaps and the
 *   gradient vanishes for a frame. See the token's comment.
 * · **All FOUR of the specimen's shadows are drawn, and two of them are a coloured glow the rest
 *   of this app is still forbidden.** The specimen's button carries
 *
 *       inset 0  1px 0      rgba(255,255,255,.30)   → `--edge-cta-lip-top`, on the border
 *       inset 0 -1px 0      rgba(0,0,0,.14)         → `--edge-cta-lip-bottom`, on the border
 *       0 1px 2px           rgba(196,58,10,.26)     → `--shadow-cta-ember`
 *       0 5px 12px -4px     rgba(196,58,10,.30)     → `--shadow-cta-ember`
 *
 *   and every one of them is here. **THE OWNER LIFTED THE COLOURED-GLOW BAN FOR THIS COMPONENT ON
 *   2 SEPTEMBER 2026**, asked directly whether he was lifting it for the primary given that it was
 *   the last remaining difference against KANON: "ma być identycznie jak w kanon". This file
 *   previously said the outer pair was refused and that lifting the ban was the owner's call and
 *   nobody else's. He made it.
 *
 *   **It is an exception, not a repeal.** The ban is one of the oldest standing directives in this
 *   repo (CLAUDE.md, owner, 2 August 2026) and it holds everywhere else without change: no
 *   coloured blur on a popup, a dialog, a focus treatment, a card, a drag preview or a chart. Do
 *   not spread `--shadow-cta-ember` to another component, and do not restore a coloured shadow
 *   somewhere it was removed under the ban — this exception covers the ember primary and the `+`
 *   button that wears it, and nothing else. CLAUDE.md's own wording still reads as absolute; that
 *   is the owner's document to amend and this comment is the record until he does.
 *
 *   On 23 September 2026 the owner confirmed the exception and asked that it follow the trainer's
 *   brand colour: under a brand the token is rewritten in a deeper shade of the brand's own solid,
 *   same geometry and alphas (`entities/brand-color/model/brandTokens.ts`).
 *
 *   Nothing moves in Z. The shadow is a cast lift on the RESTING state and never changes on hover
 *   or press, so the lift gesture the view ADR rejects is still not made.
 *
 * `prefers-reduced-motion` needs nothing here — `tokens/base.css` collapses every transition
 * duration in the app under it, and a feature stylesheet is forbidden its own media query. */

.ember {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  font-family: var(--font-body);
  white-space: nowrap;
  color: var(--on-accent-grad);
  background: var(--accent-grad);

  /* ── The gradient spans the BORDER box, and leaving it to the default was a visible defect ──
   * `background-origin` defaults to `padding-box` while `background-clip` defaults to `border-box`,
   * so the gradient was laid out across 34px and painted across 36 — and a background that does not
   * fill its paint area REPEATS. The 1px border ring therefore showed the neighbouring tile: the
   * gradient's 100% stop (#ef3d14, near-red) along the TOP edge and its 0% stop (#ff8a45, pale
   * orange) along the BOTTOM. Two mis-coloured hairlines on every primary in the app, measured on
   * the running roster at 4x on 1 September 2026 and matching the owner's report of "stripes on the
   * plus" exactly. `border-box` makes the layout box and the paint box the same, so there is no
   * second tile to show. */
  background-origin: border-box;

  /* Declared in parts rather than as a shorthand: the value allowlist requires a TOKEN colour in a
   * `border` shorthand — correctly, since a literal could ride in the same way. The edge itself
   * exists so a filled primary and an outlined ghost beside it are the same size; every surface
   * pins its own `height` and `box-sizing` is `border-box`, so what it actually buys is WIDTH
   * parity on a content-sized button. `transparent` is the default for the two sides the specimen
   * says nothing about; the other two carry the lip, below. */
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;

  /* The specimen's two INSET shadows, drawn on the edge the border already reserves — see the
   * header, and `--edge-cta-lip-top`'s own note for why they are not `box-shadow: inset`. The two
   * side borders stay transparent: the specimen lights the top and darkens the bottom, and a lip
   * running round all four sides is a bevel. */
  border-top-color: var(--edge-cta-lip-top);
  border-bottom-color: var(--edge-cta-lip-bottom);

  /* The specimen's other two, cast in the accent's own hue. See the header: this is the ONE place
   * in the app a coloured shadow is drawn, by the owner's word of 2 September 2026. A cast shadow
   * is painted from the BORDER box, which is the box the specimen casts from, so the 1px border
   * above does not move it. */
  box-shadow: var(--shadow-cta-ember);
  cursor: pointer;

  /* `filter` only. The gradient stays painted; see the header. */
  transition-duration: var(--dur-fast);
  transition-property: filter;
  transition-timing-function: var(--ease);
}

/* The fill without the cast glow, for a primary that sits inside a card (owner, 29 October 2026). */
.ember.flat {
  box-shadow: none;
}

/* The PILL primary (owner, 3 November 2026, `docs/design/2026-11-03-pulpit-e3d/`): the same three
 * stops laid corner to corner, flat — no lip on the border, no cast under it. Geometry stays the
 * surface's like every other primary's; the full radius is part of that geometry. The disabled
 * treatment below still wins over it, so a pill that refuses reads exactly as any primary does.
 * The Pulpit wears it first; the app-wide switch is a separate change. */
.ember.pill {
  background: var(--accent-grad-diagonal);
  border-color: transparent;
  box-shadow: none;
}

.ember.pill:hover:not([aria-disabled='true'], :disabled) {
  filter: var(--cta-lift);
}

.ember.pill:active:not([aria-disabled='true'], :disabled) {
  filter: var(--cta-press);
}

.ember:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

/* NOT the `disabled` attribute's business and not `pointer-events: none`: a control that carries a
 * reason has to stay in the tab order for the reason to be reachable at all (view ADR §12). It takes
 * the neutral treatment, so the ember appears only on the frames where pressing it would do
 * something — which is also what keeps the accent a scalpel. */
.ember[aria-disabled='true'],
.ember:disabled {
  color: var(--text-2);
  background: var(--bg-chip);
  border-color: var(--border);

  /* The lift goes with the fill. A neutral chip casting the accent's own hue would be an ember
   * glow under a control that is refusing to do anything. */
  box-shadow: none;
  cursor: not-allowed;
}

/* `:not(:disabled)` as well as `:not([aria-disabled])`, because both spellings exist in this app:
 * a control whose refusal carries an explanation stays focusable and uses the attribute, and one
 * with nothing to explain uses the property. Neither should darken under the pointer. */
.ember:hover:not([aria-disabled='true'], :disabled) {
  filter: var(--cta-press);
}
